'use client';

import { useState } from 'react';
import { Check, Share2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';

interface Props {
  /** Profile owner's display name — used as the share-sheet title. */
  name: string;
}

/**
 * Share the current dancer profile (BAL-248 §5d — the button previously had
 * an empty onClick). Uses the Web Share API where available (mobile + some
 * desktop browsers); otherwise copies the URL to the clipboard and shows a
 * brief "Copied" confirmation. A cancelled native share sheet is a no-op
 * (we do NOT silently copy in that case).
 */
export function ShareButton({ name }: Props) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const onShare = async () => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    if (!url) return;

    const shareData = {
      title: t('dancer:publicView.shareButton.shareTitle', { name }),
      url,
    };

    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        // User cancelled or the share failed — intentional no-op.
      }
      return;
    }

    // No Web Share API → clipboard fallback with transient confirmation.
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (insecure context / permissions) — no-op.
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      onClick={onShare}
      aria-label={t('dancer:publicView.shareButton.ariaLabel')}
      className="hover:text-foreground inline-flex h-auto items-center gap-2 p-0 transition-colors"
    >
      {copied ? <Check className="h-3 w-3" /> : <Share2 className="h-3 w-3" />}
      {copied
        ? t('dancer:publicView.shareButton.copied')
        : t('dancer:publicView.shareButton.share')}
    </Button>
  );
}
